import { type Control, type FieldValues, useWatch } from "react-hook-form";
import {
	Resources,
	useResource,
} from "web_myplann/Infrastructure/Translations/Resources";
import {
	type InstructionTypeLabels,
	useInstructionCurrencyOptions,
	useInstructionFrequencyOptions,
	useInstructionTypeOptions,
} from "web_myplann/Portfolio/Forms/ToolsShared/useInstructionOptions";
import { getPortfolioCurrencySign } from "web_myplann/Portfolio/Helpers/getPortfolioCurrencySign";
import { FlatRowFormDatePicker } from "web_myplann/Shared/Components/FlatRow/FlatRowFormDatePicker";
import { FlatRowFormInput } from "web_myplann/Shared/Components/FlatRow/FlatRowFormInput";
import { FlatRowFormSelect } from "web_myplann/Shared/Components/FlatRow/FlatRowFormSelect";
import { DateView } from "web_myplann/Shared/Types/DateView";
import { DateFormat } from "web_myplann/Shared/Utils/dateFormats";

type Props = {
	control: Control<FieldValues>;
	typeLabels?: InstructionTypeLabels;
	showCount?: boolean;
};

const PageResources = Resources.Portfolio.AddTools;
const Placeholders = Resources.Portfolio.AddShared.Placeholders;

export const InstructionFields = ({
	control,
	typeLabels,
	showCount,
}: Props) => {
	const { t } = useResource();

	const typeOptions = useInstructionTypeOptions(typeLabels);
	const frequencyOptions = useInstructionFrequencyOptions();
	const currencyOptions = useInstructionCurrencyOptions();
	const [currency, startDate] = useWatch({
		control,
		name: ["currency", "date"],
	});

	return (
		<>
			<FlatRowFormSelect
				control={control}
				name="investmentType"
				label={t(PageResources.Fields.instructionType)}
				options={typeOptions}
				placeholder={t(Placeholders.select)}
			/>
			<FlatRowFormDatePicker
				control={control}
				name="date"
				label={t(PageResources.Fields.startAt)}
				views={[DateView.Year, DateView.Month, DateView.Day]}
				displayFormat={DateFormat.Display}
				placeholder={t(Placeholders.select)}
			/>
			<FlatRowFormDatePicker
				control={control}
				name="endedAt"
				label={t(PageResources.Fields.endAt)}
				views={[DateView.Year, DateView.Month, DateView.Day]}
				displayFormat={DateFormat.Display}
				minDate={startDate ?? undefined}
				placeholder={t(Placeholders.select)}
			/>
			<FlatRowFormInput
				control={control}
				name="amount"
				type="float"
				label={t(PageResources.Fields.amount)}
				placeholder={t(Placeholders.fill)}
				suffix={getPortfolioCurrencySign(t, currency)}
			/>
			<FlatRowFormSelect
				control={control}
				name="currency"
				label={t(PageResources.Fields.currency)}
				options={currencyOptions}
			/>
			{showCount && (
				<FlatRowFormInput
					control={control}
					name="count"
					type="float"
					decimalPlaces={8}
					label={t(PageResources.Fields.count)}
					placeholder={t(Placeholders.fill)}
				/>
			)}
			<FlatRowFormSelect
				control={control}
				name="frequency"
				label={t(PageResources.Fields.frequency)}
				options={frequencyOptions}
				placeholder={t(Placeholders.select)}
				isLast
			/>
		</>
	);
};
